import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Admin-only: post free-text notices that scroll in the global Updates Ticker.
// Rendered as a compact header button that opens a manage dialog.
export default function BroadcastManager() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/broadcasts");
      setItems(data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/broadcasts", { text: text.trim(), link: link.trim(), expires_on: expiresOn || null });
      setText(""); setLink(""); setExpiresOn("");
      toast.success("Broadcast posted to the ticker");
      window.dispatchEvent(new Event("ticker:refresh"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post broadcast");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setItems((p) => p.filter((b) => b.id !== id));
    try { await api.delete(`/admin/broadcasts/${id}`); window.dispatchEvent(new Event("ticker:refresh")); } catch { load(); }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="broadcast-toggle"
        className="rounded-full border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 gap-1.5"
        title="Post a school-wide notice that scrolls in the ticker"
      >
        <Megaphone className="h-4 w-4" />
        <span className="hidden sm:inline">Broadcast</span>
        {items.length > 0 && (
          <span className="ml-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300" data-testid="broadcast-active-count">{items.length}</span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="broadcast-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <span className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center"><Megaphone className="h-4 w-4" /></span>
              Broadcast to ticker
            </DialogTitle>
            <DialogDescription>Post a school-wide notice that scrolls for everyone in the Updates ticker.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") post(); }}
                placeholder="e.g. PTM this Friday at 10 AM"
                maxLength={240}
                className="flex-1"
                data-testid="broadcast-text-input"
              />
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Link (optional) e.g. /events"
                className="sm:w-52"
                data-testid="broadcast-link-input"
              />
              <Input
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
                title="Auto-remove from the ticker after this date (optional)"
                className="sm:w-40"
                data-testid="broadcast-expiry-input"
              />
              <Button onClick={post} disabled={busy || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="broadcast-post-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Post
              </Button>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">Set an optional expiry date and the notice disappears from the ticker automatically once it passes.</p>

            {items.length > 0 && (
              <ul className="space-y-1.5" data-testid="broadcast-list">
                {items.map((b) => (
                  <li key={b.id} className="flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-slate-700/40 px-3 py-2" data-testid={`broadcast-item-${b.id}`}>
                    <Megaphone className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span className="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-200 truncate">{b.text}{b.link ? <span className="text-slate-400"> · {b.link}</span> : null}{b.expires_on ? <span className="text-amber-500"> · until {b.expires_on}</span> : null}</span>
                    <button
                      type="button"
                      onClick={() => remove(b.id)}
                      className="shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                      title="Remove broadcast"
                      data-testid={`broadcast-remove-${b.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
